import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { CircleAlert, ClipboardList, CloudSun, ExternalLink, ShieldCheck, Droplet, FileText, Wrench } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT, DEMO_MAINTENANCE, DEMO_SQUAWKS } from '../../lib/demoData';
import { mxStatus, isDeferred } from '../../lib/maintenance';
import type { AircraftDoc, MaintenanceItemDoc, SquawkDoc } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';
import { TRACKED_REG } from '../../lib/flightTrack';
import { isStudentPilot } from '../../lib/eligibility';

type Row = { letter: string; label: string; status: string; tone: 'ok' | 'warning' | 'danger' | 'muted' };

export function Preflight() {
  const { demoMode, profile } = useAuth();
  const [aircraft, setAircraft] = useState<AircraftDoc[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceItemDoc[]>([]);
  const [squawks, setSquawks] = useState<SquawkDoc[]>([]);
  const [airport, setAirport] = useState('KLGB');

  useEffect(() => {
    if (demoMode) {
      setAircraft(DEMO_AIRCRAFT);
      setMaintenance(DEMO_MAINTENANCE);
      setSquawks(DEMO_SQUAWKS);
      return;
    }
    if (!db) return;
    const a = onSnapshot(collection(db, 'aircraft'), (s) => setAircraft(s.docs.map((d) => d.data() as AircraftDoc)));
    const m = onSnapshot(collection(db, 'maintenance'), (s) => setMaintenance(s.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id }))));
    const q = onSnapshot(collection(db, 'squawks'), (s) => setSquawks(s.docs.map((d) => d.data() as SquawkDoc)));
    return () => { a(); m(); q(); };
  }, [demoMode]);

  const ac = aircraft[0];
  const grounded = squawks.some((s) => s.aircraftId === ac?.aircraftId && s.isGrounding && s.status !== 'resolved');
  const mxOverdue = ac ? maintenance.some((m) => m.isAirworthiness && mxStatus(m, ac.currentTach).level === 3) : false;
  const airworthy = !grounded && !mxOverdue;

  const aviates = useMemo<Row[]>(() => {
    const tach = ac?.currentTach ?? 0; // maintenance hours track on tach
    const find = (re: RegExp) => maintenance.find((m) => re.test(m.label));
    // A date-tracked line from a maintenance item (or sample fallback).
    const dateRow = (letter: string, label: string, item: typeof maintenance[number] | undefined): Row => {
      if (!item?.dueDate) return { letter, label, status: 'Not tracked', tone: 'muted' };
      const days = Math.round((new Date(`${item.dueDate}T12:00:00`).getTime() - Date.now()) / 86400000);
      return {
        letter,
        label,
        status: days < 0 ? `Overdue ${Math.abs(days)}d` : `Current · due ${item.dueDate}`,
        tone: days < 0 ? 'danger' : days < 30 ? 'warning' : 'ok',
      };
    };
    // An hours-tracked line. A held item reads as scheduled, not overdue.
    const hoursRow = (letter: string, label: string, item: typeof maintenance[number] | undefined): Row => {
      if (!item || typeof item.dueHobbs !== 'number') return { letter, label, status: '—', tone: 'ok' };
      if (isDeferred(item)) {
        const withAnnual = item.deferredUntil === ac?.annualDueDate;
        return {
          letter,
          label,
          status: withAnnual ? `With annual · ${item.deferredUntil}` : `Scheduled · ${item.deferredUntil}`,
          tone: 'ok',
        };
      }
      const hrs = +(item.dueHobbs - tach).toFixed(1);
      return {
        letter,
        label,
        status: hrs < 0 ? `Due ${Math.abs(hrs)} hr ago` : `${hrs} hr to next`,
        tone: hrs < 0 ? 'danger' : hrs <= 10 ? 'warning' : 'ok',
      };
    };

    const annualDays = ac?.annualDueDate ? Math.round((new Date(ac.annualDueDate).getTime() - Date.now()) / 86400000) : null;
    // Supplemental / ADs — show the most urgent AD-type hours item (seat rail, ignition).
    const adItems = maintenance
      .filter((m) => !isDeferred(m) && typeof m.dueHobbs === 'number' && /seat rail|ignition|\bAD\b/i.test(m.label))
      .map((m) => ({ m, hrs: +((m.dueHobbs as number) - tach).toFixed(1) }))
      .sort((a, b) => a.hrs - b.hrs);
    const ad = adItems[0];

    return [
      {
        letter: 'A',
        label: 'Annual inspection',
        status: annualDays == null ? '—' : annualDays < 0 ? `Overdue ${Math.abs(annualDays)}d` : `Due ${ac?.annualDueDate} · ${annualDays}d`,
        tone: annualDays == null ? 'ok' : annualDays < 0 ? 'danger' : annualDays < 30 ? 'warning' : 'ok',
      },
      // Not tracked in the portal, and deliberately not called "n/a": whether a VOR
      // check is required is the pilot's call under 91.171, not the app's. The signed
      // sheet lives in the flight log binder in the airplane.
      { letter: 'V', label: 'VOR check (30 days, IFR)', status: 'In the binder', tone: 'muted' },
      hoursRow('I', 'Oil change', find(/oil/i)),
      dateRow('A', 'Altimeter & static (24 mo)', find(/altimeter/i)),
      dateRow('T', 'Transponder (24 mo)', find(/transponder/i)),
      dateRow('E', 'ELT 12-mo inspection (91.207d)', find(/elt/i)),
      ad
        ? {
            letter: 'S',
            label: `Supplemental / ADs (${ad.m.label})`,
            status: ad.hrs < 0 ? `Due ${Math.abs(ad.hrs)} hr ago` : `${ad.hrs} hr to next`,
            tone: ad.hrs < 0 ? 'danger' : ad.hrs <= 10 ? 'warning' : 'ok',
          }
        : { letter: 'S', label: 'Supplemental / ADs', status: 'Not tracked', tone: 'muted' },
    ];
  }, [ac, maintenance]);

  // Maintenance records — every tracked item that has been signed off, newest first,
  // with the shop's notes and the scanned logbook entry where one is on file. This is
  // the pilot-facing view of the same records the owner keeps under Aircraft.
  const records = useMemo(
    () =>
      maintenance
        .filter((m) => m.lastDoneDate)
        .sort((a, b) => (b.lastDoneDate || '').localeCompare(a.lastDoneDate || '')),
    [maintenance]
  );
  const fmtDate = (d?: string) =>
    d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <>
      <PageHeader title="Pre-flight" subtitle={`Check airworthiness and status before you fly ${TRACKED_REG}.`} />
      {isStudentPilot(profile?.pilot) && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Student pilot:</strong> before takeoff, make sure{' '}
          <Link to="/bookings" className="underline">My bookings</Link> shows this flight approved by your
          instructor. No approval, no flight. That&rsquo;s an insurance condition.
        </div>
      )}

      {/* Advisory status banner — NOT an airworthiness determination. The app
          only reflects grounding squawks + tracked maintenance; the PIC alone
          determines airworthiness (14 CFR 91.7). */}
      <div className={`card p-4 ${airworthy ? 'ring-1 ring-emerald-200' : 'ring-2 ring-red-300'}`}>
        <div className="flex items-center gap-3">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${airworthy ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {airworthy ? <ShieldCheck size={20} /> : <CircleAlert size={20} />}
          </span>
          <div>
            <div className="font-display text-base font-semibold text-navy-900">
              {airworthy ? 'No grounding or overdue items flagged*' : grounded ? 'Grounded — do not fly' : 'Airworthiness item overdue'}
            </div>
            <div className="text-sm text-ink-muted">
              {airworthy
                ? 'No open grounding squawks or overdue airworthiness items in the app.'
                : grounded
                ? 'An open grounding squawk must be resolved by the owner.'
                : 'An airworthiness item is overdue — contact the owner.'}
            </div>
          </div>
        </div>
        <p className="mt-3 border-t border-slate-100 pt-3 text-xs leading-relaxed text-ink-muted">
          *Advisory only. This reflects grounding squawks and maintenance tracked in the app — it is <span className="font-semibold">not</span> an airworthiness determination. The pilot in command is solely responsible for determining the aircraft is airworthy before every flight (14 CFR 91.7).
        </p>
      </div>

      {/* AVIATES checklist */}
      <div className="card mt-4 p-5">
        <div className="text-xs font-medium uppercase tracking-wider text-ink-subtle">AVIATES — airworthiness inspections</div>
        <ul className="mt-3 space-y-2 text-sm">
          {aviates.map((row) => (
            <li key={row.label} className="flex items-center gap-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-sky-50 text-xs font-bold text-navy-700">
                {row.letter}
              </span>
              <span className="flex-1 text-ink">
                {row.label}
              </span>
              <span
                className={`text-xs font-medium ${
                  row.tone === 'danger'
                    ? 'text-red-700'
                    : row.tone === 'warning'
                      ? 'text-amber-700'
                      : row.tone === 'muted'
                        ? 'text-ink-subtle'
                        : 'text-emerald-700'
                }`}
              >
                {row.status}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-ink-muted">
          Items are live from the aircraft’s maintenance tracking and are advisory — the aircraft
          logbooks are the official record, and the airworthiness determination is yours as PIC. The ELT
          date reflects inspection with the last annual; the battery replacement date is in the
          Maintenance records below. The VOR check isn’t tracked here: there’s a sheet in the flight
          log binder in the airplane, and if you’re filing IFR that’s where you log it.
        </p>
      </div>

      {/* Current state + weather */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="card p-5">
          <div className="text-xs font-medium uppercase tracking-wider text-ink-subtle">Last recorded</div>
          <div className="mt-2 flex gap-8">
            <div>
              <div className="text-xs text-ink-muted">Hobbs</div>
              <div className="font-display text-xl font-semibold text-navy-900">{ac?.currentHobbs.toFixed(1) ?? '—'}</div>
            </div>
            <div>
              <div className="text-xs text-ink-muted">Tach</div>
              <div className="font-display text-xl font-semibold text-navy-900">{ac?.currentTach.toFixed(1) ?? '—'}</div>
            </div>
          </div>
          {ac?.oilSpec && (
            <div className="mt-3 flex items-start gap-2 border-t border-slate-100 pt-3 text-sm">
              <Droplet size={15} className="mt-0.5 shrink-0 text-amber-600" />
              <span>
                <span className="text-ink-muted">Approved oil: </span>
                <span className="font-medium text-navy-900">{ac.oilSpec}</span>
              </span>
            </div>
          )}
        </div>
        <div className="card p-5">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-ink-subtle">
            <CloudSun size={14} /> Weather
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              className="input max-w-[110px] uppercase"
              value={airport}
              onChange={(e) => setAirport(e.target.value.toUpperCase())}
            />
            <a
              href={`https://aviationweather.gov/data/metar/?ids=${airport}`}
              target="_blank"
              rel="noopener"
              className="btn-primary inline-flex items-center gap-1.5 text-sm"
            >
              <ExternalLink size={14} /> METAR / TAF
            </a>
          </div>
        </div>
      </div>

      {/* Maintenance records */}
      <div id="maintenance-records" className="card mt-4 p-5">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-ink-subtle">
          <Wrench size={14} /> Maintenance records
        </div>
        {records.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">No signed-off maintenance on file yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {records.map((m) => (
              <li key={m.itemId} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="font-medium text-navy-900">{m.label}</span>
                  <span className="text-xs text-ink-muted">
                    {fmtDate(m.lastDoneDate)}
                    {typeof m.lastDoneHobbs === 'number' ? ` · ${m.lastDoneHobbs.toFixed(1)} tach` : ''}
                    {m.dueDate ? ` · next due ${fmtDate(m.dueDate)}` : typeof m.dueHobbs === 'number' ? ` · next due ${m.dueHobbs.toFixed(1)} tach` : ''}
                  </span>
                </div>
                {m.notes && <p className="mt-1 text-sm leading-relaxed text-ink-muted">{m.notes}</p>}
                {m.docUrl && (
                  <a
                    href={m.docUrl}
                    target="_blank"
                    rel="noopener"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:underline"
                  >
                    <FileText size={14} /> View signed logbook entry
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-ink-muted">
          Transcribed from the aircraft logbooks; the logbooks in the airplane remain the official record.
        </p>
      </div>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/squawks/new" className="btn-secondary inline-flex items-center gap-1.5 text-sm">
          <CircleAlert size={15} /> Report a squawk
        </Link>
        <Link to="/flights/new" className="btn-secondary inline-flex items-center gap-1.5 text-sm">
          <ClipboardList size={15} /> Log flight
        </Link>
      </div>
    </>
  );
}
